/**
 * Terminal Supervisor — deterministic watch / interpret / self-heal planning
 * around Brunel's ONE existing PTY + transport stack (issue #444).
 *
 * Architecture:
 *   deterministic terminal telemetry
 *   → low-cost anomaly interpretation
 *   → Core Observer / Overseer finding (propose work; do not patch app code)
 *
 * Self-heal actions are planned here; callers apply only deterministically safe
 * transport/PTY repairs. Never kill a healthy PTY just to freshen AI context.
 * Overseer dispatch remains canonical for Worker fix tasks.
 */

import {
  automationMayWrite,
  ownerKeyboardPriorityActive,
  terminalHolderIsPerson,
} from './hold-policy.js';

export type TerminalProcessTruth =
  | 'not_open'
  | 'opening'
  | 'running'
  | 'waiting'
  | 'exited';

export type TerminalTransportTruth =
  | 'live'
  | 'reconnecting'
  | 'secure_fallback'
  | 'connecting_internal';

export type TerminalSupervisorAnomalyKind =
  | 'false_owner_hold_refusal'
  | 'stale_owner_hold'
  | 'sequence_gap'
  | 'transport_flapping'
  | 'fake_connecting_without_terminal'
  | 'pty_dead_while_session_open'
  | 'repeated_held_refusal'
  | 'input_ack_ambiguity';

export type TerminalSelfHealActionKind =
  | 'reconnect_transport'
  | 'resubscribe'
  | 'catchup_sequence'
  | 'restart_transport_binding'
  | 'reopen_terminal'
  | 'none';

export interface TerminalSupervisorTelemetry {
  readonly sessionId: string | null;
  readonly hasTerminal: boolean;
  readonly process: TerminalProcessTruth;
  readonly transport: TerminalTransportTruth;
  readonly inputHeld: boolean;
  readonly inputHolder: string | null;
  /** ISO held-until when known; null when free or unknown. */
  readonly heldUntilIso: string | null;
  readonly latestSeq: number;
  readonly highestRenderedSeq: number;
  readonly hasSequenceGap: boolean;
  readonly ptyAlive: boolean;
  readonly reconnectAttempt: number;
  /** Count of consecutive automation refusals for held-by-owner in this window. */
  readonly heldRefusalCount: number;
  readonly nowMs: number;
}

export interface TerminalSupervisorAnomaly {
  readonly kind: TerminalSupervisorAnomalyKind;
  readonly sessionId: string | null;
  readonly detail: string;
  readonly evidence: Readonly<Record<string, string | number | boolean | null>>;
}

export interface TerminalSelfHealPlan {
  readonly action: TerminalSelfHealActionKind;
  /** Catch-up starts after this seq when action is catchup_sequence. */
  readonly catchupFromSeq?: number;
  readonly reason: string;
}

export interface TerminalOverseerWorkProposal {
  readonly title: string;
  readonly evidence: string;
  readonly suggestedTask: string;
  readonly anomalyKind: TerminalSupervisorAnomalyKind;
}

export interface TerminalSupervisorFinding {
  readonly controlId: string;
  readonly title: string;
  readonly detail: string;
  readonly anomaly: TerminalSupervisorAnomaly;
  readonly proposedWork: TerminalOverseerWorkProposal | null;
}

const FINDING_COOLDOWN_MS = 30_000;
const TRANSPORT_FLAP_ATTEMPTS = 4;

/** Map owner-facing process text into supervisor process truth. */
export function processTruthFromStatus(
  hasTerminal: boolean,
  status: string | null,
  closedAt: string | null = null,
): TerminalProcessTruth {
  if (!hasTerminal) return 'not_open';
  if (closedAt !== null || status === 'closed' || status === 'exited' || status === 'failed' || status === 'error') {
    return 'exited';
  }
  if (status === 'idle' || status === 'waiting_for_input' || status === 'waiting_for_approval') return 'waiting';
  if (status === 'running' || status === 'closing') return 'running';
  return 'opening';
}

export function transportTruthFromState(
  hasTerminal: boolean,
  state: 'connecting' | 'live' | 'reconnecting' | 'fallback',
): TerminalTransportTruth {
  if (!hasTerminal) return 'connecting_internal';
  if (state === 'live') return 'live';
  if (state === 'reconnecting') return 'reconnecting';
  if (state === 'connecting') return 'connecting_internal';
  return 'secure_fallback';
}

/**
 * Interpret telemetry into anomalies. Pure: no I/O, no LLM.
 */
export function interpretTerminalTelemetry(
  telemetry: TerminalSupervisorTelemetry,
): readonly TerminalSupervisorAnomaly[] {
  const out: TerminalSupervisorAnomaly[] = [];
  const sessionId = telemetry.sessionId;

  if (!telemetry.hasTerminal && telemetry.transport === 'connecting_internal') {
    // Internal connecting without a terminal must never be shown as owner-facing Connecting.
    // This is a watch signal when UI would otherwise invent that state.
    out.push({
      kind: 'fake_connecting_without_terminal',
      sessionId,
      detail: 'Transport is in internal connecting while no terminal exists',
      evidence: { hasTerminal: false, transport: telemetry.transport },
    });
  }

  if (telemetry.hasTerminal && !telemetry.ptyAlive && telemetry.process !== 'exited' && telemetry.process !== 'not_open') {
    out.push({
      kind: 'pty_dead_while_session_open',
      sessionId,
      detail: 'Session row still open but local PTY is not alive',
      evidence: { process: telemetry.process, ptyAlive: false },
    });
  }

  if (telemetry.hasSequenceGap) {
    out.push({
      kind: 'sequence_gap',
      sessionId,
      detail: `Missing terminal output between rendered ${telemetry.highestRenderedSeq} and latest ${telemetry.latestSeq}`,
      evidence: {
        highestRenderedSeq: telemetry.highestRenderedSeq,
        latestSeq: telemetry.latestSeq,
        hasSequenceGap: true,
      },
    });
  }

  if (telemetry.reconnectAttempt >= TRANSPORT_FLAP_ATTEMPTS
      && (telemetry.transport === 'reconnecting' || telemetry.transport === 'secure_fallback')) {
    out.push({
      kind: 'transport_flapping',
      sessionId,
      detail: `Transport reconnect attempts reached ${telemetry.reconnectAttempt}`,
      evidence: {
        reconnectAttempt: telemetry.reconnectAttempt,
        transport: telemetry.transport,
      },
    });
  }

  // Stale / false owner hold: holder name looks like owner but authoritative held is false,
  // or repeated refusals while automationMayWrite would now allow.
  if (
    terminalHolderIsPerson(telemetry.inputHolder)
    && telemetry.inputHeld === false
    && telemetry.heldRefusalCount > 0
  ) {
    out.push({
      kind: 'false_owner_hold_refusal',
      sessionId,
      detail: 'Automation refused for owner_terminal while authoritative hold is already free (idle/expired)',
      evidence: {
        inputHolder: telemetry.inputHolder,
        inputHeld: false,
        heldRefusalCount: telemetry.heldRefusalCount,
      },
    });
  }

  if (
    ownerKeyboardPriorityActive({ held: telemetry.inputHeld, holder: telemetry.inputHolder })
    && telemetry.heldUntilIso !== null
  ) {
    const until = Date.parse(telemetry.heldUntilIso);
    if (Number.isFinite(until) && until <= telemetry.nowMs) {
      out.push({
        kind: 'stale_owner_hold',
        sessionId,
        detail: 'Owner hold timestamp is in the past but held flag still true',
        evidence: {
          heldUntilIso: telemetry.heldUntilIso,
          inputHolder: telemetry.inputHolder,
          inputHeld: telemetry.inputHeld,
        },
      });
    }
  }

  if (telemetry.heldRefusalCount >= 2) {
    out.push({
      kind: 'repeated_held_refusal',
      sessionId,
      detail: `Automation write refused ${telemetry.heldRefusalCount} times for input hold`,
      evidence: {
        heldRefusalCount: telemetry.heldRefusalCount,
        inputHeld: telemetry.inputHeld,
        inputHolder: telemetry.inputHolder,
        automationMayWrite: automationMayWrite({
          held: telemetry.inputHeld,
          holder: telemetry.inputHolder,
        }),
      },
    });
  }

  return out;
}

/**
 * Plan a deterministically safe self-heal for one anomaly.
 * Never plans killing a healthy PTY.
 */
export function planTerminalSelfHeal(
  anomaly: TerminalSupervisorAnomaly,
  telemetry: TerminalSupervisorTelemetry,
): TerminalSelfHealPlan {
  switch (anomaly.kind) {
    case 'sequence_gap':
      return {
        action: 'catchup_sequence',
        catchupFromSeq: telemetry.highestRenderedSeq,
        reason: 'Fill missing output sequences from durable store',
      };
    case 'transport_flapping':
      return {
        action: 'restart_transport_binding',
        reason: 'Bounded reconnect exhausted; restart the transport binding',
      };
    case 'fake_connecting_without_terminal':
      return {
        action: 'none',
        reason: 'No terminal exists; do not invent Connecting or reopen',
      };
    case 'pty_dead_while_session_open':
      return {
        action: 'reopen_terminal',
        reason: 'PTY is truly dead; reopen only the broken terminal',
      };
    case 'false_owner_hold_refusal':
    case 'stale_owner_hold':
      return {
        action: 'none',
        reason: 'Hold lifecycle is authoritative in DB; refresh hold read, do not kill PTY',
      };
    case 'repeated_held_refusal':
      return {
        action: 'none',
        reason: 'Emit finding for Overseer; do not force-clear an active owner hold',
      };
    case 'input_ack_ambiguity':
      return {
        action: 'none',
        reason: 'Ambiguous live ACK must not replay keystrokes',
      };
    default:
      return { action: 'none', reason: 'No deterministic heal' };
  }
}

/** Prefer reconnect/resubscribe when transport is down but PTY is healthy. */
export function planTransportRecover(telemetry: TerminalSupervisorTelemetry): TerminalSelfHealPlan {
  if (!telemetry.hasTerminal) {
    return { action: 'none', reason: 'No terminal to recover' };
  }
  if (telemetry.transport === 'live') {
    return { action: 'none', reason: 'Transport already live' };
  }
  if (telemetry.transport === 'reconnecting') {
    return { action: 'reconnect_transport', reason: 'Continue bounded reconnect backoff' };
  }
  if (telemetry.hasSequenceGap) {
    return {
      action: 'catchup_sequence',
      catchupFromSeq: telemetry.highestRenderedSeq,
      reason: 'Secure fallback with sequence gap — catch up first',
    };
  }
  return { action: 'resubscribe', reason: 'Resubscribe private terminal channel' };
}

/** The Observer ingest doors keep at most this many controlId characters. */
const CONTROL_ID_MAX = 80;

export function terminalSupervisorControlId(kind: TerminalSupervisorAnomalyKind, sessionId: string | null): string {
  // core:* is the existing durable Observer Core materialization boundary.
  const prefix = `core:terminal:${kind}:`;
  const session = sessionId?.trim() ?? '';
  if (session === '') return `${prefix}none`;
  // Keep the whole session identity: an 8-character prefix let two sessions share one
  // finding. A UUID that does not fit whole loses only its hyphens, never a character.
  if (prefix.length + session.length <= CONTROL_ID_MAX) return `${prefix}${session}`;
  return `${prefix}${session.replaceAll('-', '')}`;
}

export function findingFromAnomaly(anomaly: TerminalSupervisorAnomaly): TerminalSupervisorFinding {
  const proposedWork = shouldProposeOverseerWork(anomaly)
    ? {
        title: `Terminal supervisor: ${anomaly.kind}`,
        evidence: anomaly.detail,
        suggestedTask: overseerTaskFor(anomaly.kind),
        anomalyKind: anomaly.kind,
      }
    : null;
  return {
    controlId: terminalSupervisorControlId(anomaly.kind, anomaly.sessionId),
    title: `Terminal Supervisor: ${anomaly.kind.replaceAll('_', ' ')}`,
    detail: anomaly.detail,
    anomaly,
    proposedWork,
  };
}

function shouldProposeOverseerWork(anomaly: TerminalSupervisorAnomaly): boolean {
  return anomaly.kind === 'false_owner_hold_refusal'
    || anomaly.kind === 'repeated_held_refusal'
    || anomaly.kind === 'pty_dead_while_session_open'
    || anomaly.kind === 'transport_flapping';
}

function overseerTaskFor(kind: TerminalSupervisorAnomalyKind): string {
  switch (kind) {
    case 'false_owner_hold_refusal':
    case 'stale_owner_hold':
    case 'repeated_held_refusal':
      return 'Inspect terminal input-hold lifecycle and Worker persistent attach refusals; keep owner keyboard priority while idle owner holds do not block automation.';
    case 'pty_dead_while_session_open':
      return 'Investigate Worker terminal session open while local PTY is dead; reopen only if PTY is truly broken.';
    case 'transport_flapping':
      return 'Investigate terminal Realtime transport flapping and secure-fallback catch-up.';
    default:
      return 'Review correlated terminal supervisor evidence and dispatch a Worker fix only if code change is required.';
  }
}

export function shouldEmitTerminalFinding(
  lastEmittedAt: ReadonlyMap<string, number>,
  controlId: string,
  nowMs: number,
  cooldownMs = FINDING_COOLDOWN_MS,
): boolean {
  const last = lastEmittedAt.get(controlId);
  if (last === undefined) return true;
  return nowMs - last >= cooldownMs;
}

/**
 * Record a held-refusal observation for supervisor correlation (#394 #71 pattern).
 */
export function noteHeldRefusal(input: {
  readonly message: string;
  readonly previousCount: number;
}): { readonly count: number; readonly looksLikeOwnerHold: boolean } {
  const looksLikeOwnerHold = /held by owner_terminal|held by owner_composer|held by owner:/i.test(input.message);
  return {
    count: input.previousCount + 1,
    looksLikeOwnerHold,
  };
}
